import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PalmettoComboBox, PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { z } from 'zod';

import {
  ChecklistConditionName,
  ChecklistConditionNameLabels,
  ChecklistConditionOperator,
  ChecklistDecisionType,
  ChecklistDecisionTypeLabels,
  ChecklistEvaluationKey,
  ChecklistItemInputType,
  ChecklistReferenceKey,
  documentTitles,
  AllDocumentTypesArr,
  ParameterizedConditions,
  ProgramType,
  RequirementFlagSettingsDocument,
} from 'types';

import { DialogFormValues, InputOptionFormValue } from './checklistItemInputOptions';
import { TemplateItemFormValue } from './ChecklistTemplateItemsEditor';
import { ConditionChip } from './ConditionChip';
import { evaluationTemplateRegistry } from './evaluationTemplateRegistry';
import {
  buildOptionPayload,
  exposesDecisionOptions,
  seedFlagMappings,
  stripOptionFlagMappings,
} from './flagMappingOptions';
import { FlagMappingErrorsProvider, FlagSettingsProvider } from './FlagSettingsContext';
import { InputFieldRow } from './InputFieldRow';
import { parseParamsRaw, serializeParamsRaw } from './paramsRaw';

export interface ChecklistItemEditFormProps {
  item: TemplateItemFormValue | null;
  onSave: (item: TemplateItemFormValue) => void;
  onCancel: () => void;
  programTypes: string[];
}

const documentTypeOptions = AllDocumentTypesArr.map((value) => ({
  value,
  label: documentTitles[value],
}));

const FLAG_REQUIREMENT_REQUIRED_MESSAGE = 'Flag Requirement is required.';

const isFlagMappingRequirementIssue = (path: PropertyKey[]): boolean =>
  path[0] === 'inputs' &&
  path[2] === 'options' &&
  path[4] === 'flagMappings' &&
  path[6] === 'requirementFlagSettingId';

export const buildValidationSchema = (programTypes: string[]) => {
  const conditionSchema = z.object({
    name: z.string().min(1, 'Please select a condition type or remove the empty condition.'),
    operator: z.nativeEnum(ChecklistConditionOperator).default(ChecklistConditionOperator.Is),
    paramsRaw: z.string(),
  });

  const baseInputSchema = z
    .object({
      label: z.string().min(1, 'Input label is required.'),
      type: z.enum(ChecklistItemInputType),
      required: z.boolean(),
      options: z.array(
        z.object({
          label: z.string(),
          value: z.string(),
          decisionType: z.enum(Object.keys(ChecklistDecisionTypeLabels) as [string, ...string[]]).optional(),
          requirementFlagSettingId: z.string().optional(),
          flagMappings: z
            .array(
              z.object({
                requirementFlagSettingId: z.string().optional(),
                criterionId: z.string().optional(),
                problemId: z.string().optional(),
              }),
            )
            .optional(),
        }),
      ),
      referenceKey: z.nativeEnum(ChecklistReferenceKey).optional(),
      requirementFlagSettingId: z.string().optional(),
      conditions: z.array(conditionSchema).default([]),
      evaluationKey: z.nativeEnum(ChecklistEvaluationKey).optional(),
      evaluationParams: z.record(z.string(), z.unknown()).optional(),
    })
    .refine((input) => !exposesDecisionOptions(input.type, input.evaluationKey) || input.options.length > 0, {
      error: 'Select inputs must have at least one option.',
    })
    .superRefine((input, ctx) => {
      if (!exposesDecisionOptions(input.type, input.evaluationKey)) return;
      input.options.forEach((option, optionIndex) => {
        const mappings = option.flagMappings ?? [];
        mappings.forEach((mapping, mappingIndex) => {
          if (!mapping.requirementFlagSettingId) {
            ctx.addIssue({
              code: 'custom',
              message: FLAG_REQUIREMENT_REQUIRED_MESSAGE,
              path: ['options', optionIndex, 'flagMappings', mappingIndex, 'requirementFlagSettingId'],
            });
          }
        });
        const hasSkippedLevel = mappings.some(
          (m) => (m.criterionId && !m.requirementFlagSettingId) || (m.problemId && !m.criterionId),
        );
        if (hasSkippedLevel) {
          ctx.addIssue({
            code: 'custom',
            message: 'A flag mapping needs a Flag Requirement before a Flag, and a Flag before an Issue.',
            path: ['options', optionIndex],
          });
        }
      });
    })
    .refine(
      (input) => {
        if (input.type === ChecklistItemInputType.referenceValue) {
          return !!input.referenceKey;
        }
        return true;
      },
      { message: 'Reference key is required for reference value inputs.', path: ['referenceKey'] },
    )
    .refine(
      (input) => {
        if (input.type === ChecklistItemInputType.evaluation) {
          return !!input.evaluationKey;
        }
        return true;
      },
      { message: 'Evaluation type is required for evaluation inputs.', path: ['evaluationKey'] },
    );

  // Each evaluation key with a descriptor contributes its own required-params check; keys
  // registered as `null` (e.g. serialValidation) are left unvalidated here.
  const inputSchema = Object.values(evaluationTemplateRegistry).reduce((schema, descriptor) => {
    if (!descriptor) return schema;
    return descriptor.validations.reduce(
      (currentSchema, validation) =>
        currentSchema.refine(
          (input) => {
            if (input.type !== ChecklistItemInputType.evaluation) return true;
            if (input.evaluationKey !== descriptor.key) return true;

            return validation.validate(input.evaluationParams);
          },
          {
            message: validation.message,
            path: ['evaluationParams'],
          },
        ),
      schema,
    );
  }, baseInputSchema);

  return z
    .object({
      name: z.string(),
      description: z.string(),
      conditions: z.array(conditionSchema),
      inputs: z.array(inputSchema),
      documentTypes: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      for (let i = 0; i < data.conditions.length; i++) {
        const condition = data.conditions[i];
        const conditionName = condition.name as ChecklistConditionName;

        if (ParameterizedConditions.has(conditionName)) {
          const params = condition.paramsRaw ? parseParamsRaw(condition.paramsRaw) : [];

          if (params.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `"${ChecklistConditionNameLabels[conditionName]}" requires at least one value.`,
              path: ['conditions', i, 'paramsRaw'],
            });
            continue;
          }

          if (conditionName === ChecklistConditionName.state) {
            const invalid = params.find((p) => !/^[A-Z]{2}$/.test(p));
            if (invalid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `"${invalid}" is not a valid 2-letter state code.`,
                path: ['conditions', i, 'paramsRaw'],
              });
            }
          }

          if (conditionName === ChecklistConditionName.programType && programTypes.length > 0) {
            const invalid = params.find((p) => !programTypes.includes(p));
            if (invalid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `"${invalid}" is not in the selected program types for this template.`,
                path: ['conditions', i, 'paramsRaw'],
              });
            }
          }
        }
      }

      for (let i = 0; i < data.inputs.length; i++) {
        const inputConditions = data.inputs[i].conditions;
        for (let j = 0; j < inputConditions.length; j++) {
          const condition = inputConditions[j];
          const conditionName = condition.name as ChecklistConditionName;

          if (ParameterizedConditions.has(conditionName)) {
            const params = condition.paramsRaw ? parseParamsRaw(condition.paramsRaw) : [];

            if (params.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `"${ChecklistConditionNameLabels[conditionName]}" requires at least one value.`,
                path: ['inputs', i, 'conditions', j, 'paramsRaw'],
              });
              continue;
            }

            if (conditionName === ChecklistConditionName.state) {
              const invalid = params.find((p) => !/^[A-Z]{2}$/.test(p));
              if (invalid) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: `"${invalid}" is not a valid 2-letter state code.`,
                  path: ['inputs', i, 'conditions', j, 'paramsRaw'],
                });
              }
            }

            if (conditionName === ChecklistConditionName.programType && programTypes.length > 0) {
              const invalid = params.find((p) => !programTypes.includes(p));
              if (invalid) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: `"${invalid}" is not in the selected program types for this template.`,
                  path: ['inputs', i, 'conditions', j, 'paramsRaw'],
                });
              }
            }
          }
        }
      }
    });
};

export const ChecklistItemEditForm = ({ item, onSave, onCancel, programTypes }: ChecklistItemEditFormProps) => {
  const order = item?.order || 0;

  const { data: flagSettingsData } = useGetRequirementFlagSettingsQuery({
    type: undefined,
    pageSize: 1000,
    programTypes: programTypes.length > 0 ? (programTypes as ProgramType[]) : undefined,
  });

  const flagSettings = useMemo<RequirementFlagSettingsDocument[]>(
    () => flagSettingsData?.requirementFlagSettings ?? [],
    [flagSettingsData],
  );

  const flagSettingOptions = useMemo(() => {
    return flagSettings
      .map((s) => ({ label: s.name, value: s.id?.toString() ?? '' }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [flagSettings]);

  const defaultValues: DialogFormValues = item
    ? {
        name: item.name,
        description: item.description,
        tooltipText: item.tooltipText ?? undefined,
        conditions: (item.conditions || []).map((c) => ({
          name: c.name,
          operator: c.operator ?? ChecklistConditionOperator.Is,
          paramsRaw: serializeParamsRaw(c.params || []),
        })),
        inputs: (item.inputs || []).map((i) => ({
          key: i.key,
          label: i.label,
          type: i.type,
          required: i.required ?? false,
          options: (i.options || []).map((o) => ({
            ...o,
            decisionType: o.decisionType as ChecklistDecisionType | undefined,
            flagMappings: seedFlagMappings(o as InputOptionFormValue),
          })),
          referenceKey: i.referenceKey,
          requirementFlagSettingId: i.requirementFlagSettingId,
          conditions: (i.conditions || []).map((c) => ({
            name: c.name,
            operator: c.operator ?? ChecklistConditionOperator.Is,
            paramsRaw: serializeParamsRaw(c.params || []),
          })),
          tooltipText: i.tooltipText ?? undefined,
          evaluationKey: i.evaluationKey,
          evaluationParams: i.evaluationParams,
        })),
        documentTypes: item.documentTypes ?? [],
      }
    : {
        name: '',
        description: '',
        tooltipText: undefined,
        conditions: [],
        inputs: [],
        documentTypes: [],
      };

  const { control, handleSubmit, setValue, getValues } = useForm<DialogFormValues>({ defaultValues });

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({ control, name: 'conditions' });

  const {
    fields: inputFields,
    append: appendInput,
    remove: removeInput,
    move: moveInput,
  } = useFieldArray({ control, name: 'inputs' });

  const inputPointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const inputKeyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const inputSensors = useSensors(inputPointerSensor, inputKeyboardSensor);

  const handleInputDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = inputFields.findIndex((f) => f.id === String(active.id));
      const newIndex = inputFields.findIndex((f) => f.id === String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        moveInput(oldIndex, newIndex);
      }
    },
    [inputFields, moveInput],
  );

  const [conditionError, setConditionError] = useState('');
  const [inputsError, setInputsError] = useState('');
  const [flagMappingErrors, setFlagMappingErrors] = useState<Record<string, string>>({});

  const handleAddCondition = useCallback(() => {
    appendCondition({
      name: '',
      operator: ChecklistConditionOperator.Is,
      paramsRaw: '',
    });
  }, [appendCondition]);

  const handleAddInput = useCallback(() => {
    appendInput({
      key: `input-${crypto.randomUUID()}`,
      label: '',
      type: ChecklistItemInputType.text,
      required: false,
      options: [],
      referenceKey: undefined,
      conditions: [],
    });
  }, [appendInput]);

  // A single outside click produces both a mousedown and a blur, either of which can close the
  // form; these refs keep that pair (and repeated Save clicks) from saving the item twice.
  const isClosingRef = useRef(false);
  const hasClosedRef = useRef(false);

  const onSubmit = handleSubmit(() => {
    // Use getValues() instead of handleSubmit's `data` argument: a useFieldArray row's
    // Controller-adjacent fields (like evaluationParams) can go stale in handleSubmit's snapshot
    // if this component hasn't re-rendered since a sibling field update, even though the live
    // form store is correct.
    const data = getValues();
    const schema = buildValidationSchema(programTypes);
    const result = schema.safeParse(data);

    if (!result.success) {
      const conditionIssue = result.error.issues.find((issue) => issue.path[0] === 'conditions');
      const inputIssue = result.error.issues.find(
        (issue) => issue.path[0] === 'inputs' && !isFlagMappingRequirementIssue(issue.path),
      );

      const mappingErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        if (!isFlagMappingRequirementIssue(issue.path)) continue;
        const [, inputIndex, , optionIndex, , mappingIndex] = issue.path;
        mappingErrors[
          `inputs.${String(inputIndex)}.options.${String(optionIndex)}.flagMappings.${String(mappingIndex)}`
        ] = issue.message;
      }

      setConditionError(conditionIssue?.message || '');
      setInputsError(inputIssue?.message || '');
      setFlagMappingErrors(mappingErrors);
      return;
    }

    setConditionError('');
    setInputsError('');
    setFlagMappingErrors({});
    hasClosedRef.current = true;

    onSave({
      key: item?.key || `item-${crypto.randomUUID()}`,
      name: data.name,
      description: data.description,
      tooltipText: data.tooltipText || undefined,
      order,
      conditions:
        result.data.conditions.length > 0
          ? result.data.conditions.map((c) => ({
              name: c.name,
              operator: c.operator,
              params: c.paramsRaw ? parseParamsRaw(c.paramsRaw) : undefined,
            }))
          : undefined,
      inputs:
        data.inputs.length > 0
          ? data.inputs.map((input) => ({
              key: input.key || `input-${crypto.randomUUID()}`,
              label: input.label,
              type: input.type,
              required:
                input.type !== ChecklistItemInputType.referenceValue &&
                input.type !== ChecklistItemInputType.evaluation
                  ? input.required || undefined
                  : undefined,
              options:
                (input.type === ChecklistItemInputType.requirement ||
                  input.type === ChecklistItemInputType.evaluation) &&
                input.options.length > 0
                  ? input.options.map(
                      exposesDecisionOptions(input.type, input.evaluationKey)
                        ? buildOptionPayload
                        : stripOptionFlagMappings,
                    )
                  : undefined,
              referenceKey: input.referenceKey,
              requirementFlagSettingId: input.requirementFlagSettingId || undefined,
              conditions:
                input.conditions.length > 0
                  ? input.conditions.map((c) => ({
                      name: c.name,
                      operator: c.operator,
                      params: c.paramsRaw ? parseParamsRaw(c.paramsRaw) : undefined,
                    }))
                  : undefined,
              tooltipText: input.tooltipText || undefined,
              evaluationKey:
                input.type === ChecklistItemInputType.evaluation ? input.evaluationKey || undefined : undefined,
              evaluationParams:
                input.type === ChecklistItemInputType.evaluation ? input.evaluationParams : undefined,
            }))
          : undefined,
      documentTypes:
        result.data.documentTypes && result.data.documentTypes.length > 0 ? result.data.documentTypes : undefined,
      _stableId: item?._stableId || crypto.randomUUID(),
    });
  });

  const canAddCondition = conditionFields.length < Object.keys(ChecklistConditionNameLabels).length;

  const containerRef = useRef<HTMLDivElement>(null);
  // Captured at mount so a later `item` identity change can't re-trigger the scroll.
  const isNewItemRef = useRef(!item);

  useEffect(() => {
    if (isNewItemRef.current) {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, []);

  const handleClose = useCallback(() => {
    if (isClosingRef.current || hasClosedRef.current) return;
    isClosingRef.current = true;
    const name = getValues('name').trim();
    if (!name) {
      hasClosedRef.current = true;
      onCancel();
      return;
    }
    // Release on settle, not on next render: a failed submit only re-renders the Controller
    // owning the invalid field, never this component, so a render-driven reset stays latched and
    // blocks every later close attempt. A successful submit latches hasClosedRef instead.
    void onSubmit().finally(() => {
      isClosingRef.current = false;
    });
  }, [getValues, onSubmit, onCancel]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [handleClose]);

  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (e.relatedTarget !== null && !containerRef.current?.contains(e.relatedTarget)) {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <FlagSettingsProvider value={flagSettings}>
      <FlagMappingErrorsProvider value={flagMappingErrors}>
        <div ref={containerRef} onBlur={handleContainerBlur} className="flex flex-col gap-8">
          <div className="flex items-center gap-8">
            <PalmettoIcon name="penEdit" size="sm" className="shrink-0 text-text-tertiary" />
            <div className="min-w-0 flex-1">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <PalmettoTextInput
                    label="Name"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
            <div className="flex shrink-0 items-center gap-8">
              <button
                type="button"
                onClick={onCancel}
                aria-label="Discard changes"
                className="cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
              >
                <PalmettoIcon name="lineArrowCurveUpLeft" size="sm" />
              </button>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Save changes"
                className="text-interactive-primary hover:text-interactive-primary-hover cursor-pointer border-none bg-transparent p-0 leading-none"
              >
                <PalmettoIcon name="check" size="sm" />
              </button>
            </div>
          </div>

          {conditionFields.map((conditionField, conditionIndex) => (
            <div key={conditionField.id} className="flex items-center gap-4 pl-[34px]">
              <span className={`text-sm font-medium ${conditionIndex === 0 ? 'invisible' : 'text-text-tertiary'}`}>
                and
              </span>
              <ConditionChip
                fieldPrefix="conditions"
                conditionIndex={conditionIndex}
                control={control}
                getValues={getValues}
                setValue={setValue}
                removeCondition={removeCondition}
                programTypes={programTypes}
              />
            </div>
          ))}

          {canAddCondition && (
            <div className="pl-[34px]">
              <button
                type="button"
                onClick={handleAddCondition}
                className="text-interactive-primary hover:text-interactive-primary-hover inline-flex cursor-pointer items-center gap-4 border-none bg-transparent p-0 text-sm leading-none font-medium"
              >
                <PalmettoIcon name="add" size="xs" />
                Condition
              </button>
            </div>
          )}

          {conditionError && <div className="text-feedback-error text-xs">{conditionError}</div>}

          {inputFields.length > 0 && (
            <div className="pl-[34px]">
              <div className="mb-4 text-sm font-bold">Inputs & Reference Data</div>
              <DndContext sensors={inputSensors} collisionDetection={closestCenter} onDragEnd={handleInputDragEnd}>
                <SortableContext items={inputFields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  {inputFields.map((inputField, inputIndex) => (
                    <InputFieldRow
                      key={inputField.id}
                      id={inputField.id}
                      inputIndex={inputIndex}
                      control={control}
                      getValues={getValues}
                      setValue={setValue}
                      removeInput={removeInput}
                      flagSettingOptions={flagSettingOptions}
                      programTypes={programTypes}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {inputsError && <div className="text-feedback-error pl-[34px] text-xs">{inputsError}</div>}

          <div className="flex items-center gap-2 pl-[34px]">
            <button
              type="button"
              onClick={handleAddInput}
              className="text-interactive-primary hover:text-interactive-primary-hover inline-flex cursor-pointer items-center gap-4 border-none bg-transparent p-0 text-sm leading-none font-medium"
            >
              <PalmettoIcon name="add" size="xs" />
              Input
            </button>
          </div>

          <div className="pl-[34px]">
            <Controller
              name="documentTypes"
              control={control}
              render={({ field }) => (
                <PalmettoComboBox
                  id="documentTypes"
                  label="Document Types"
                  helperText="Documents relevant to this checklist item."
                  items={documentTypeOptions
                    .map((opt) => ({
                      ...opt,
                      selected: field.value?.includes(opt.value) ?? false,
                    }))
                    .toSorted((a, b) => a.label.localeCompare(b.label))}
                  onValueChange={(details) => field.onChange(details.value)}
                  maxMenuHeight={320}
                />
              )}
            />
          </div>

          <div className="pl-[34px]">
            <Controller
              name="description"
              control={control}
              rules={{ required: 'Description is required' }}
              render={({ field, fieldState }) => (
                <PalmettoTextInput
                  label="Description"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  validationMessage={
                    fieldState.error ? { type: 'error', text: fieldState.error.message ?? '' } : undefined
                  }
                />
              )}
            />
          </div>

          {/* Tooltip Text */}
          <div className="pl-[34px]">
            <Controller
              name="tooltipText"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  label="Tooltip Text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  helperText="Guidance shown to QC users during review"
                />
              )}
            />
          </div>
        </div>
      </FlagMappingErrorsProvider>
    </FlagSettingsProvider>
  );
};
